import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import type {
  CoachPatientListRow,
  PatientGroup,
} from "@care-by-ella/contracts";
import { PatientGroupBadge } from "@/components/PatientGroupBadge";
import { OnboardingStatusCell } from "@/components/OnboardingStatusCard";
import {
  fetchCoachPatients,
  fetchPatients,
  UnauthorizedError,
  type PatientListRow,
} from "@/lib/clinician-api";
import { patientListRangeLabel } from "@/lib/patient-list-page";
import {
  coachListDay,
  PATIENT_LIST_COLUMN_LABEL,
  patientListColumns,
  type PatientListColumn,
} from "@/lib/patient-list-columns";
import { treatmentStateLabel } from "@/lib/state-labels";
import { useSupportView, useViewPath } from "@/components/SupportView";

// CAR-13 — clinic patient list + search. The inbox is the triage queue;
// this is the "find any patient" surface. Search is by name or by digits of
// the personal number (debounced server round-trip).
//
// TES-1308 — the server pages the list and applies both the search and the
// journey filter over the whole clinic, so the chip counts and "x av y" are
// the clinic's, not the page's.
//
// TES-1209 — the coach (and the admin's coach view) reads her own variant:
// the same roster and search, with the latest check-in, open cases and
// latest contact in place of the doctor's declaration and registration
// columns (patient-list-columns.ts).

// The coach's columns are present only on her rows.
type ListRow = PatientListRow & Partial<CoachPatientListRow>;

// Only the chip COLOURS live here — the words come from the shared label map,
// so the patient list and the patient page can never disagree about what a
// treatment state is called.
const NEUTRAL_CHIP = "bg-surfaceMuted text-textSecondary border-surfaceMuted";
const TREATMENT_CHIP_CLASS: Record<string, string> = {
  no_treatment: NEUTRAL_CHIP,
  onboarding: "bg-warning/10 text-warning border-warning/30",
  active: "bg-success/10 text-success border-success/30",
  paused: "bg-warning/10 text-warning border-warning/30",
  ended: NEUTRAL_CHIP,
};

function treatmentChip(state: string) {
  return (
    <span
      className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border ${
        TREATMENT_CHIP_CLASS[state] ?? NEUTRAL_CHIP
      }`}
    >
      {treatmentStateLabel(state)}
    </span>
  );
}

function shortDate(ts: string | null) {
  if (!ts) return "—";
  return ts.slice(0, 10);
}

function Cell({ column, row }: { column: PatientListColumn; row: ListRow }) {
  switch (column) {
    case "patient":
      return (
        <td className="px-md py-sm">
          <div>{row.name ?? "—"}</div>
          <div className="font-mono text-xs text-textSecondary">
            …{row.personalNumberLast6}
          </div>
        </td>
      );
    case "journey":
      return (
        <td className="px-md py-sm">
          <PatientGroupBadge group={row.patientGroup} />
        </td>
      );
    // TES-1117 — the precise status next to the journey; "Onboarding" in
    // the next column is only the grouping.
    case "status":
      return (
        <td className="px-md py-sm">
          <OnboardingStatusCell status={row.onboardingStatus} />
        </td>
      );
    case "treatment":
      return (
        <td className="px-md py-sm">{treatmentChip(row.treatmentState)}</td>
      );
    case "latestDeclaration":
      return (
        <td className="px-md py-sm text-textSecondary">
          {row.hasIntake ? shortDate(row.lastSubmissionAt) : "—"}
        </td>
      );
    case "registered":
      return (
        <td className="px-md py-sm text-textSecondary">
          {shortDate(row.createdAt)}
        </td>
      );
    case "latestCheckin":
      return (
        <td className="px-md py-sm text-textSecondary">
          {coachListDay(row.latestCheckinAt)}
        </td>
      );
    case "openCases":
      return (
        <td
          className={`px-md py-sm tabular-nums ${
            row.openCases ? "font-medium" : "text-textSecondary"
          }`}
        >
          {row.openCases ?? "—"}
        </td>
      );
    case "latestContact":
      return (
        <td className="px-md py-sm text-textSecondary">
          {coachListDay(row.latestContactAt)}
        </td>
      );
  }
}

export function PatientsRoute() {
  const { signOut, claims } = useAuth();
  const viewPath = useViewPath();
  // TES-1209 — a coach, or an admin in her coach view, reads the coach list.
  const supportView = useSupportView()?.viewAs ?? null;
  const view: "doctor" | "coach" =
    claims?.role === "coach" || supportView === "coach" ? "coach" : "doctor";
  const columns = patientListColumns(view);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<ListRow[] | null>(null);
  // TES-474 #5 — the same Resa filter the inbox has. A doctor looking for a
  // transfer patient should find her in the view called "Patienter".
  const [group, setGroup] = useState<PatientGroup | "ALL">("ALL");
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<{
    total: number;
    limit: number;
    groupCounts: Record<PatientGroup, number>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Debounce the search so each keystroke doesn't fire a request. 250ms is
  // snappy enough for a doctor typing the last few PN digits.
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // A new search, journey or view starts again from the first page.
  useEffect(() => setOffset(0), [query, group, view]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    // A response that arrives after the next request went out is dropped, so
    // a slow page cannot overwrite a newer one.
    let stale = false;
    debounce.current = setTimeout(() => {
      setLoading(true);
      setError(null);
      const params = {
        q: query,
        group: group === "ALL" ? undefined : group,
        offset,
      };
      (view === "coach" ? fetchCoachPatients(params) : fetchPatients(params))
        .then((r) => {
          if (stale) return;
          setRows(r.rows);
          setPage({
            total: r.total,
            limit: r.limit,
            groupCounts: r.groupCounts,
          });
        })
        .catch((e) => {
          if (stale) return;
          if (e instanceof UnauthorizedError) {
            void signOut();
            return;
          }
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (!stale) setLoading(false);
        });
    }, 250);
    return () => {
      stale = true;
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query, group, offset, signOut, view]);

  const transferCount = page?.groupCounts.PRESCRIPTION_TRANSFER ?? 0;
  const newCount = page?.groupCounts.NEW_TREATMENT ?? 0;
  const range = page
    ? patientListRangeLabel(offset, rows?.length ?? 0, page.total)
    : null;
  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Patienter</h1>
        <span className="text-sm text-textSecondary">
          {loading ? "Hämtar…" : page ? `${page.total} patienter` : ""}
        </span>
      </div>
      {transferCount + newCount > 0 ? (
        <div className="flex gap-xs" role="group" aria-label="Filtrera på resa">
          {(
            [
              ["ALL", `Alla (${transferCount + newCount})`],
              ["PRESCRIPTION_TRANSFER", `Receptövertag (${transferCount})`],
              ["NEW_TREATMENT", `Ny behandling (${newCount})`],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setGroup(value)}
              aria-pressed={group === value}
              className={`text-xs font-medium rounded px-sm py-[6px] border transition ${
                group === value
                  ? "bg-brand/10 border-brand/50 text-brand"
                  : "bg-surface border-surfaceMuted text-textSecondary hover:bg-surfaceMuted/40"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}

      <input
        type="search"
        inputMode="numeric"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Sök på namn eller personnummer"
        className="w-full max-w-sm rounded-md border border-surfaceMuted bg-surface px-md py-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand/40"
      />

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          {query.trim()
            ? "Inga patienter matchar sökningen."
            : group !== "ALL"
              ? "Inga patienter på den resan."
              : "Inga patienter i kliniken än."}
        </div>
      ) : null}

      {rows && rows.length > 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md overflow-x-auto">
          <table className="w-full min-w-[44rem] text-sm">
            <thead className="bg-surfaceMuted/60 text-textSecondary uppercase tracking-wide text-xs">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="text-left px-md py-sm font-medium">
                    {PATIENT_LIST_COLUMN_LABEL[c]}
                  </th>
                ))}
                <th className="px-md py-sm" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.patientId}
                  className="border-t border-surfaceMuted hover:bg-surfaceMuted/40 transition"
                >
                  {columns.map((c) => (
                    <Cell key={c} column={c} row={row} />
                  ))}
                  <td className="px-md py-sm text-right">
                    <Link
                      to={viewPath(`/patients/${row.patientId}`)}
                      className="text-brand hover:underline text-sm font-medium"
                    >
                      Öppna →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {page && range ? (
        <div className="flex items-center justify-between gap-md text-sm text-textSecondary">
          <span>{range}</span>
          <div className="flex gap-xs">
            <button
              type="button"
              disabled={loading || offset === 0}
              onClick={() => setOffset(Math.max(0, offset - page.limit))}
              className="rounded border border-surfaceMuted bg-surface px-sm py-[6px] font-medium disabled:opacity-50 hover:bg-surfaceMuted/40"
            >
              Föregående
            </button>
            <button
              type="button"
              disabled={loading || offset + page.limit >= page.total}
              onClick={() => setOffset(offset + page.limit)}
              className="rounded border border-surfaceMuted bg-surface px-sm py-[6px] font-medium disabled:opacity-50 hover:bg-surfaceMuted/40"
            >
              Nästa
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
